import React from "react";
import dayjs from "dayjs";

interface RecordedStaff {
  name?: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
}

export interface RecordedCareMetadata {
  createdBy?: RecordedStaff | string | null;
  updatedBy?: RecordedStaff | string | null;
  createdAt?: unknown;
  updatedAt?: unknown;
}

export function recordedStaffName(staff: RecordedCareMetadata["createdBy"]) {
  if (!staff || typeof staff !== "object") return "Not recorded";
  return (
    (typeof staff.name === "string" ? staff.name.trim() : "") ||
    [staff.firstName, staff.middleName, staff.lastName]
      .filter((part) => typeof part === "string" && part.trim())
      .map((part) => part!.trim())
      .join(" ") ||
    "Not recorded"
  );
}

export function recordedTime(value: unknown) {
  if (typeof value !== "string" || !value.trim() || !dayjs(value).isValid())
    return "Not recorded";
  return dayjs(value).format("DD/MM/YYYY HH:mm");
}

// Read-only server provenance. Never substitute reviewer, viewer or clinical dates.
export default function RecordedMetadata({
  record,
}: {
  record?: RecordedCareMetadata | null;
}) {
  if (!record) return null;
  const fields = [
    ["Created by", recordedStaffName(record.createdBy), "last-update"],
    ["Created at", recordedTime(record.createdAt), "last-date"],
    ["Last updated by", recordedStaffName(record.updatedBy), "last-update"],
    ["Last updated at", recordedTime(record.updatedAt), "last-date"],
  ];
  return (
    <dl
      aria-label="Recorded metadata"
      style={{ margin: 0, minWidth: 0, textAlign: "right" }}
    >
      {fields.map(([label, value, className]) => (
        <div
          key={label}
          className={className}
          style={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "flex-end",
            gap: "0.5em",
            margin: 0,
          }}
        >
          <dt>{label}</dt>
          <dd style={{ margin: 0, overflowWrap: "anywhere" }}>{value}</dd>
        </div>
      ))}
    </dl>
  );
}
